import PropTypes from 'prop-types';
import { BsTrash, BsListUl, BsX } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

// The saturated pill would swallow the palette variants, so the buttons carry
// their own light-on-dark classes.
const DEFAULT_ENTITY_LABEL = { singular: 'Eintrag', plural: 'Einträge' };

const ACTION_BUTTON_CLASS = '!border-transparent !bg-transparent !text-white/90 hover:!bg-white/10 hover:!text-white';

/**
 * Selection toolbar for a table: renders nothing until rows are selected, then
 * overlays a purple pill at the bottom of the table area. Visual shell only —
 * all behavior is passed in as callbacks, so every selectable table shares it.
 *
 * Pass `actions` for feature-specific buttons; only the actions shared by most
 * tables (contact list, delete) stay named so their look cannot drift.
 */
const BulkActionsBar = ({
	selectedCount,
	entityLabel = DEFAULT_ENTITY_LABEL,
	actions = [],
	onClear,
	onAddToContactList,
	onDelete,
}) => {
	if (selectedCount === 0) return null;

	return (
		<div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center px-4">
			<div className="pointer-events-auto flex flex-wrap items-center gap-1 rounded-2xl bg-primary-500 px-3 py-2 shadow-[0_18px_40px_-12px_rgba(123,102,255,0.65)] ring-1 ring-white/10 animate-[fadeIn_0.15s_ease]">
				<span className="px-2 font-medium text-white">
					<span className="mr-1.5 rounded-md bg-white/15 px-1.5 py-0.5 font-semibold tabular-nums">
						{selectedCount}
					</span>
					{selectedCount !== 1 ? entityLabel.plural : entityLabel.singular} ausgewählt
				</span>
				<span className="mx-1 h-5 w-px bg-white/20" />
				{onAddToContactList && (
					<Button className={ACTION_BUTTON_CLASS} onClick={onAddToContactList}>
						<BsListUl />
						Zu Kontaktliste
					</Button>
				)}
				{actions.map((action) => (
					// Disabled buttons fire no hover events, so the tooltip sits on a wrapper.
					<span key={action.key} title={action.title} className="inline-flex">
						<Button className={ACTION_BUTTON_CLASS} disabled={action.disabled} onClick={action.onClick}>
							{action.icon}
							{action.label}
						</Button>
					</span>
				))}
				{onDelete && (
					<Button className={ACTION_BUTTON_CLASS} onClick={onDelete}>
						<BsTrash />
						Löschen
					</Button>
				)}
				<span className="mx-1 h-5 w-px bg-white/20" />
				<button
					onClick={onClear}
					className="rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
					title="Auswahl aufheben"
					aria-label="Auswahl aufheben"
				>
					<BsX className="h-5 w-5" />
				</button>
			</div>
		</div>
	);
};

BulkActionsBar.propTypes = {
	selectedCount: PropTypes.number.isRequired,
	/** e.g. useScopedSelection's `entityLabel` */
	entityLabel: PropTypes.shape({ singular: PropTypes.string.isRequired, plural: PropTypes.string.isRequired }),
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.node.isRequired,
			icon: PropTypes.node,
			disabled: PropTypes.bool,
			/** Tooltip, also shown while disabled (e.g. why the action is unavailable). */
			title: PropTypes.string,
			onClick: PropTypes.func.isRequired,
		}),
	),
	onClear: PropTypes.func.isRequired,
	onAddToContactList: PropTypes.func,
	onDelete: PropTypes.func,
};

export default BulkActionsBar;
